import Popper from 'popper.js';
import { debounce } from 'lodash';

import GeolocationManager from 'Lib/geolocationManager';
import * as customEvent from './events';

import Error from './error';
import SearchSuggestionItem from './suggestionItem';
import Spinner from 'CoreComponents/spinner';
import SvgIcon from 'CoreComponents/svgIcon';

// Default class names for the DOM elements of the locationInput
const CLASS_NAME = {
  BASE_WRAPPER: 'location-search',
  INPUT_CONTAINER: 'location-search__input-container',
  INPUT: 'location-search__input',
  PLACE_ID_INPUT: 'location-place-id-input',
  ICON_CONTAINER: 'location-search__icon-container',
  ICON: 'location-search__icon',
  RESET_BUTTON: 'location-search__btn-reset',
  SUGGESTION_LIST: 'location-search__suggestion-list',
  // States
  FETCHING_SUGGESTION: 'location-search--fetching',
  FILLED_SEARCH_INPUT: 'location-search--filled',
  OPEN_SUGGESTION_LIST: 'location-search--open',
  DISABLED: 'location-search--disabled'
};

// Default configs for popper
const CONFIG = {
  placement: 'bottom-end'
};

export const DEFAULT_OPTIONS = {
  className: CLASS_NAME,
  config: CONFIG,
  selector: '.location-search-input',
  enableGeolocation: false,
  suggestionTypes: ['geocode'],
  disabled: false
};

class LocationSearch {
  /**
   * Initializer
   *
   * @param {Element} elementRef - HTML node, to add locationSearch properties on.
   * @param {Object} options - Configuration options, defaults to `DEFAULT_OPTIONS`.
   * */
  constructor(elementRef, options = DEFAULT_OPTIONS) {
    const { disabled, geolocation, geolocationInput, placeIdInput } = elementRef.dataset;

    // Root element container
    this.elementRef = elementRef;
    this.searchText = this.elementRef.value;

    // Configuration
    this.options = options;
    this.className = CLASS_NAME;
    this.config = options.config || CONFIG;
    this.disabled = disabled || DEFAULT_OPTIONS['disabled'];
    this.enableGeolocation = (geolocation !== undefined && geolocationInput !== undefined) || DEFAULT_OPTIONS['enableGeolocation'];
    this.geolocationInputRef = document.querySelector(geolocationInput) || undefined;
    this.placeIdInputRef = document.querySelector(placeIdInput) || undefined;

    // Set Suggestion types
    this.suggestionTypes = DEFAULT_OPTIONS['suggestionTypes'];
    if (this.elementRef.getAttribute('data-suggestion-types')) {
      this.suggestionTypes = this.elementRef.getAttribute('data-suggestion-types').split(',');
    }

    this.geolocationManager = new GeolocationManager({ types: this.suggestionTypes });

    // Bind functions
    this.onSearchInputHandler = debounce(this.onSearchInputHandler.bind(this), 500);
    this.onBlurInputHandler = this.onBlurInputHandler.bind(this);
    this.onClickSuggestionHandler = this.onClickSuggestionHandler.bind(this);
    this.onClickResetHandler = this.onClickResetHandler.bind(this);
    this.onClickInputContainerHandler = this.onClickInputContainerHandler.bind(this);

    this.onFetchingSuggestionsHandler = this.onFetchingSuggestionsHandler.bind(this);
    this.onFetchSuggestionsSuccessHandler = this.onFetchSuggestionsSuccessHandler.bind(this);
    this.onFetchSuggestionsFailureHandler = this.onFetchSuggestionsFailureHandler.bind(this);

    this.onSelectingLocationHandler = this.onSelectingLocationHandler.bind(this);
    this.onSelectLocationSuccessHandler = this.onSelectLocationSuccessHandler.bind(this);
    this.onSelectLocationFailureHandler = this.onSelectLocationFailureHandler.bind(this);

    this.onResetLocationHandler = this.onResetLocationHandler.bind(this);

    this._setup();
  }

  // Event Handlers

  /**
   * Event handler when text is entered in the search input.
   * Triggers a request to fetch place suggestions.
   *
   * @param {Event} _event - input event on the search input.
   * */
  onSearchInputHandler(_event) {
    const inputText = this.searchInput.value;

    if ((this.searchText && !inputText)) {
      const resetLocationEvent = new CustomEvent(customEvent.LOCATION_SEARCH_RESET_LOCATION);
      this.searchInput.dispatchEvent(resetLocationEvent);
    }

    if (!inputText || (this.searchText === inputText)) {
      return;
    }

    const fetchingSuggestionsEvent = new CustomEvent(customEvent.LOCATION_SEARCH_IS_FETCHING_SUGGESTIONS);
    this.searchInput.dispatchEvent(fetchingSuggestionsEvent);

    this.geolocationManager
      .getPlaceSuggestions(inputText)
      .then((suggestions) => {
        const fetchSuccessEvent = new CustomEvent(customEvent.LOCATION_SEARCH_SUGGESTIONS_FETCH_SUCCESS, {
          detail: { suggestions }
        });
        this.searchInput.dispatchEvent(fetchSuccessEvent);
      })
      .catch((error) => {
        const fetchFailureEvent = new CustomEvent(customEvent.LOCATION_SEARCH_SUGGESTIONS_FETCH_FAILURE, {
          detail: { error: error.message }
        });
        this.searchInput.dispatchEvent(fetchFailureEvent);
      });
  }

  /**
   * Event handler when the search input has lost focus.
   *
   * @param {Event} _event - input event on the search input.
   * */
  onBlurInputHandler(_event) {
    const inputText = this.searchInput.value;
    const previousLocation = this.searchText;

    if (previousLocation && !inputText) {
      this._setLocationInputValue(previousLocation);
    }
  }

  /**
   * Event handler to select a suggestion.
   * If geolocation is enabled, triggers a request to fetch the geo-data.
   *
   * @param {Event} event - click event on the suggestion list item.
   * */
  async onClickSuggestionHandler(event) {
    const selectingSuggestionsEvent = new CustomEvent(customEvent.LOCATION_SEARCH_IS_SELECTING_LOCATION);
    this.searchInput.dispatchEvent(selectingSuggestionsEvent);

    try {
      const { id, description } = event.currentTarget.dataset;
      let eventPayload = { description: description };

      this._setPlaceIdValue(id);

      if (this.enableGeolocation) {
        const placeDetails = await this.geolocationManager.getPlaceDetails(id);
        const { geometry: { location } } = placeDetails;

        eventPayload['geolocation'] = {
          latitude: location.lat(),
          longitude: location.lng()
        };
      }

      const selectLocationSuccessEvent = new CustomEvent(customEvent.LOCATION_SEARCH_SELECT_LOCATION_SUCCESS, {
        detail: eventPayload
      });
      this.searchInput.dispatchEvent(selectLocationSuccessEvent);
    } catch (error) {
      const selectLocationFailureEvent = new CustomEvent(customEvent.LOCATION_SEARCH_SELECT_LOCATION_FAILURE, {
        detail: { error: error.message }
      });
      this.searchInput.dispatchEvent(selectLocationFailureEvent);
    }
  }

  /**
   * Event handler when the search input is cleared.
   *
   * @param {Event} _event - click event on the reset button.
   * */
  onClickResetHandler(_event) {
    this.searchInput.value = '';

    const resetLocationEvent = new CustomEvent(customEvent.LOCATION_SEARCH_RESET_LOCATION);
    this.searchInput.dispatchEvent(resetLocationEvent);
  }

  /**
   * Event handler when the search container is clicked.
   *
   * @param {Event} _event - click event on the search container element.
   * */
  onClickInputContainerHandler(_event) {
    this.searchInput.focus();
  }

  /**
   * Event handler when suggestion are being fetched.
   *
   * @param {Event} _event - custom event.
   * */
  onFetchingSuggestionsHandler(_event) {
    this._showSpinner();
    this._hideSuggestionDropdown();
    this._resetSuggestionList();
  }

  /**
   * Event handler when suggestion have been fetched sucessfully.
   *
   * @param {Event} event - custom event.
   * */
  onFetchSuggestionsSuccessHandler(event) {
    const { suggestions } = event.detail;

    this._hideSpinner();
    this._showResetButton();
    this._renderSuggestionList(suggestions);
    this._showSuggestionDropdown();
  }

  /**
   * Event handler when suggestion have NOT been fetched sucessfully.
   *
   * @param {Event} event - custom event.
   * */
  onFetchSuggestionsFailureHandler(event) {
    const { error } = event.detail;

    this._hideSpinner();
    this._showResetButton();
    this._renderError(error);
    this._showSuggestionDropdown();
  }

  /**
   * Event handler when a suggestion is being selected.
   *
   * @param {Event} _event - custom event.
   * */
  onSelectingLocationHandler(_event) {
    this._showSpinner();
  }

  /**
   * Event handler when a suggestion has been selected successfully.
   *
   * @param {Event} event - custom event.
   * */
  onSelectLocationSuccessHandler(event) {
    const { description, geolocation } = event.detail;

    this.searchText = description;
    this._setLocationInputValue(description);
    (geolocation) && this._setGeolocationInputValue(geolocation.latitude, geolocation.longitude);

    this._hideSpinner();
    this._enableSearch();
    this._showResetButton();
    this._hideSuggestionDropdown();

    // Dispatch event is bubbling outside of the component scope
    // so that screens can bind to it to implement custom callback when a location is selected
    const selectLocationCompleteEvent = new CustomEvent(customEvent.LOCATION_SEARCH_SELECT_LOCATION_COMPLETE, {
      bubbles: true
    });
    this.searchInput.dispatchEvent(selectLocationCompleteEvent);
  }

  /**
   * Event handler when a suggestion has NOT been selected successfully.
   *
   * @param {Event} event - custom event.
   * */
  onSelectLocationFailureHandler(event) {
    const { error } = event.detail;

    this._hideSpinner();
    this._enableSearch();
    this._showResetButton();
    this._renderError(error);
    this._showSuggestionDropdown();
  }

  /**
   * Event handler when the serach input is reset.
   *
   * @param {Event} _event - custom event.
   * */
  onResetLocationHandler(_event) {
    this._hideResetButton();
    this._hideSuggestionDropdown();
    this._resetSuggestionList();
    this._setLocationInputValue();
    this.enableGeolocation && this._setGeolocationInputValue();
  }

  // Private

  /**
   * Set-up the location search form.
   * */
  _setup() {
    // Hide original search input
    this.elementRef.style.display = 'none';

    // Create DOM elements
    this.baseWrapper = this._createBaseWrapper();
    this.searchContainer = this._createSearchContainer();
    this.suggestionList = this._createSuggestionList();

    this.baseWrapper.appendChild(this.searchContainer);
    this.baseWrapper.appendChild(this.suggestionList);

    // Generate dropdown
    this.popper = new Popper(this.searchContainer, this.suggestionList, this.config);

    if (this.disabled) {
      this._disableSearch();
    }

    this._addEventListeners();
  }

  /**
   * Creates the root container of the location selection container.
   *
   * @return {Element} - HTML node, root container of the location search.
   * */
  _createBaseWrapper() {
    let baseWrapper = document.createElement('div');
    baseWrapper.setAttribute('class', this.className['BASE_WRAPPER']);
    if (this.elementRef.dataset.classNames) {
      baseWrapper.classList.add(this.elementRef.dataset.classNames);
    }
    this.elementRef.parentElement.appendChild(baseWrapper);

    return baseWrapper;
  }

  /**
   * Creates the search container used to search locations.
   *
   * @return {Element} - HTML node, search container.
   * */
  _createSearchContainer() {
    this.searchContainer = document.createElement('div');
    this.searchContainer.setAttribute('class', this.className['INPUT_CONTAINER']);

    const searchIcon = SvgIcon({ name: 'icon-location', width: 18, height: 18 });
    let searchIconContainer = document.createElement('div');
    searchIconContainer.setAttribute('class', this.className['ICON_CONTAINER']);
    searchIconContainer.appendChild(searchIcon);

    const spinner = Spinner({ size: 'small' });
    let activityIconContainer = document.createElement('div');
    activityIconContainer.setAttribute('class', this.className['ICON_CONTAINER']);
    activityIconContainer.appendChild(spinner);

    const closeIcon = SvgIcon({ name: 'icon-close', width: 14, height: 14 });
    this.resetButton = document.createElement('button');
    this.resetButton.setAttribute('type', 'button');
    this.resetButton.setAttribute('class', this.className['RESET_BUTTON']);
    this.resetButton.appendChild(closeIcon);
    activityIconContainer.appendChild(this.resetButton);

    this.searchInput = document.createElement('input');
    this.searchInput.setAttribute('type', 'search');
    this.searchInput.setAttribute('class', this.className['INPUT']);
    this.searchInput.setAttribute('placeholder', this.elementRef.placeholder);
    this.searchInput.setAttribute('value', this.elementRef.value);

    this.searchContainer.appendChild(searchIconContainer);
    this.searchContainer.appendChild(this.searchInput);
    this.searchContainer.appendChild(activityIconContainer);

    return this.searchContainer;
  }

  /**
   * Creates the suggestion list container.
   *
   * @return {Element} - HTML node, suggestionList list.
   * */
  _createSuggestionList() {
    let suggestionList = document.createElement('ul');
    suggestionList.setAttribute('class', this.className['SUGGESTION_LIST']);

    return suggestionList;
  }

  /**
   * Adds all the required event listeners.
   * */
  _addEventListeners() {
    this.searchInput.addEventListener('input', this.onSearchInputHandler);
    this.searchInput.addEventListener('blur', this.onBlurInputHandler);
    this.resetButton.addEventListener('click', this.onClickResetHandler);
    this.searchContainer.addEventListener('click', this.onClickInputContainerHandler);

    this.searchInput.addEventListener(customEvent.LOCATION_SEARCH_IS_FETCHING_SUGGESTIONS, this.onFetchingSuggestionsHandler);
    this.searchInput.addEventListener(customEvent.LOCATION_SEARCH_SUGGESTIONS_FETCH_SUCCESS, this.onFetchSuggestionsSuccessHandler);
    this.searchInput.addEventListener(customEvent.LOCATION_SEARCH_SUGGESTIONS_FETCH_FAILURE, this.onFetchSuggestionsFailureHandler);

    this.searchInput.addEventListener(customEvent.LOCATION_SEARCH_IS_SELECTING_LOCATION, this.onSelectingLocationHandler);
    this.searchInput.addEventListener(customEvent.LOCATION_SEARCH_SELECT_LOCATION_SUCCESS, this.onSelectLocationSuccessHandler);
    this.searchInput.addEventListener(customEvent.LOCATION_SEARCH_SELECT_LOCATION_FAILURE, this.onSelectLocationFailureHandler);

    this.searchInput.addEventListener(customEvent.LOCATION_SEARCH_RESET_LOCATION, this.onResetLocationHandler);
  }

  _enableSearch() {
    this.baseWrapper.classList.remove(this.className['DISABLED']);
    this.searchInput.disabled = false;
  }

  _disableSearch() {
    this.baseWrapper.classList.add(this.className['DISABLED']);
    this.searchInput.disabled = true;
  }

  _showSpinner() {
    this.baseWrapper.classList.add(this.className['FETCHING_SUGGESTION']);
  }

  _hideSpinner() {
    this.baseWrapper.classList.remove(this.className['FETCHING_SUGGESTION']);
  }

  _showResetButton() {
    this.baseWrapper.classList.add(this.className['FILLED_SEARCH_INPUT']);
  }

  _hideResetButton() {
    this.baseWrapper.classList.remove(this.className['FILLED_SEARCH_INPUT']);
  }

  _showSuggestionDropdown() {
    this.baseWrapper.classList.add(this.className['OPEN_SUGGESTION_LIST']);
  }

  _hideSuggestionDropdown() {
    this.baseWrapper.classList.remove(this.className['OPEN_SUGGESTION_LIST']);
  }

  /**
   * Fill in the location inputs (initial + created by locationSearch) values.
   *
   * @return {String} - input value
   * */
  _setLocationInputValue(value = '') {
    this.elementRef.value = value;
    this.searchInput.value = value;
  }

  /**
   * Fill in the geolocation with a formatted geolocation string e.g. (10.23, 45,23).
   *
   * @return {String} - latitude
   * @return {String} - longitude
   * */
  _setGeolocationInputValue(lat = '', lng = '') {
    if (!this.geolocationInputRef) {
      return;
    }
    this.geolocationInputRef.value = lat && lng ? `(${lat}, ${lng})` : '';
  }

  _setPlaceIdValue(placeId) {
    if (!this.placeIdInputRef) {
      return;
    }

    this.placeIdInputRef.value = placeId;
  }

  /**
   * Creates the suggestion list container.
   *
   * @return {Array} - list of suggestion items.
   * */
  _renderSuggestionList(suggestions) {
    this.suggestionList.innerHTML = suggestions.map(suggestion => {
      return SearchSuggestionItem(suggestion);
    }).join(' ');

    // Bind event listners to the list items
    this.suggestionList.querySelectorAll('li').forEach(listItem => {
      listItem.addEventListener('click', this.onClickSuggestionHandler);
    });
  }

  /**
   * Removes all content in the suggestion dropdown.
   *
   * @return {String} - error message.
   * */
  _resetSuggestionList() {
    this.suggestionList.innerHTML = '';
  }

  /**
   * Renders an error messafge in the suggestion dropdown.
   *
   * @return {String} - error message.
   * */
  _renderError(message) {
    this.suggestionList.innerHTML = Error(message);
  }
}

export default LocationSearch;



// WEBPACK FOOTER //
// ./app/assets/javascripts/components/locationSearch/index.js